/**
 * Modification Option 数量控制工具函数
 * 参考 product 文件夹中的 useMod 实现
 *
 * 「这组还能不能再选」一律走 utils/sectionLimit.js 的有效上限，本文件不自己解析
 * `mod.maxOption` —— 「恰好选 N 样」的组把上限写在 minOption 上，各算各的就会出现
 * "点击处理当它是单选、数量控件当它没上限"这种自相矛盾。
 * （`option.maxOption` 是**选项自己**能点几份，与组的上限是两回事，照旧本地解析。）
 */

import { sectionIsSingleSelect, sectionMaxOption } from './sectionLimit'

/**
 * 计算已选项的总数量（考虑 number 字段）
 * @param {Array} options - 配菜选项列表
 * @returns {Number} 已选项的总数量
 */
export const calcSelectedTotal = (options = []) => options.reduce((sum, opt) => {
  if (opt.checked) {
    return sum + (opt.number > 0 ? opt.number : 1)
  }
  return sum
}, 0)

/**
 * 检查某个 option 是否可以增加数量
 * @param {Object} option - 当前选项
 * @param {Object} mod - 当前配菜组
 * @returns {Boolean} - 是否可以增加
 */
export const canIncreaseOption = (option, mod) => {
  // 1. 未选中的不能增加（必须先选中）
  if (!option.checked) {
    return false
  }

  // 2. required 类型的配菜组，每个选项只能选 1 次
  if (mod.type === 'required') {
    return false
  }

  // 3. 检查自身的 maxOption 限制
  const optionMax = Number(option.maxOption)
  const currentNumber = option.number > 0 ? option.number : 1
  if (optionMax > 0 && currentNumber >= optionMax) {
    return false
  }

  // 3. 检查上层 mod 的有效上限（exactly 组的上限写在 minOption 上，见 sectionLimit）
  const modMax = sectionMaxOption(mod)
  if (modMax !== null) {
    const selectedTotal = calcSelectedTotal(mod.options)
    // 如果增加 1 会超过上层限制，则不允许
    if (selectedTotal >= modMax) {
      return false
    }
  }

  return true
}

/**
 * 检查某个 option 是否可以减少数量
 * @param {Object} option - 当前选项
 * @param {Object} mod - 当前配菜组
 * @returns {Boolean} - 是否可以减少
 */
export const canDecreaseOption = (option, mod) => {
  // 未选中的不能减少
  if (!option.checked) {
    return false
  }

  const currentNumber = option.number > 0 ? option.number : 1

  // 如果当前数量为 1，减少后会取消选中
  // 需要检查是否是单选必选项
  if (currentNumber <= 1) {
    const isSingleRequiredExactly = mod.type === 'required' &&
      mod.limitType === 'exactly' &&
      sectionIsSingleSelect(mod)

    if (isSingleRequiredExactly) {
      return false
    }
  }

  return true
}

/**
 * 增加选项数量
 * @param {Object} option - 当前选项
 * @param {Object} mod - 当前配菜组
 * @returns {Boolean} - 是否增加成功
 */
export const increaseOptionNumber = (option, mod) => {
  if (!canIncreaseOption(option, mod)) {
    return false
  }

  if (!option.number || option.number <= 0) {
    option.number = 1
  }
  option.number += 1
  return true
}

/**
 * 减少选项数量
 * @param {Object} option - 当前选项
 * @param {Object} mod - 当前配菜组
 * @returns {Boolean} - 是否减少成功
 */
export const decreaseOptionNumber = (option, mod) => {
  if (!canDecreaseOption(option, mod)) {
    return false
  }

  const currentNumber = option.number > 0 ? option.number : 1

  if (currentNumber <= 1) {
    // 减少到 0，取消选中
    option.checked = false
    option.number = 0
    option.disabled = false
  } else {
    option.number = currentNumber - 1
  }
  return true
}

/**
 * 检查配菜组是否已达到最大可选数量
 * @param {Object} mod - 配菜组
 * @returns {Boolean} - 是否已达上限
 */
export const isModGroupMaxed = (mod) => {
  const max = sectionMaxOption(mod)
  if (max === null) return false
  const selectedTotal = calcSelectedTotal(mod.options)
  return selectedTotal >= max
}

/**
 * 判断是否为单选模式（每个选项只能选 1 次，不显示 +/- 控件）
 * - 有效上限为 1：只能选 1 个选项（含「恰好选 1 样」这类只写了 minOption 的组）
 * - required 类型：每个选项只能选择 1 次（数量固定为 1）
 * @param {Object} mod - 配菜组
 * @returns {Boolean} - 是否单选
 */
export const isRadioSelect = (mod) => {
  return sectionIsSingleSelect(mod) || mod.type === 'required'
}

/**
 * 获取选项的显示数量
 * @param {Object} option - 选项
 * @returns {Number|String} - 显示的数量或 Max 标记
 */
export const getOptionDisplayNumber = (option) => {
  const max = Number(option.maxOption)
  const current = option.number > 0 ? option.number : 1

  if (max > 0 && current >= max) {
    return `Max:(${max})`
  }
  return current
}

/**
 * 检查选项是否已达到自身最大值
 * @param {Object} option - 选项
 * @returns {Boolean} - 是否已达自身最大值
 */
export const isOptionMaxed = (option) => {
  const max = Number(option.maxOption)
  if (!max || max <= 0) return false
  const current = option.number > 0 ? option.number : 1
  return current >= max
}
